import React, { useContext, useState, useEffect } from 'react';
import { FiSave, FiShoppingBag, FiDollarSign, FiLock, FiEye, FiEyeOff, FiPhone, FiMapPin, FiCreditCard, FiCheck, FiEdit3, FiUser, FiMail, FiGlobe, FiUploadCloud, FiTrash2, FiFacebook, FiInstagram, FiX, FiClock, FiRefreshCw } from 'react-icons/fi';
import AuthContext from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import axios from 'axios';
import PaymentModal from '../components/PaymentModal';

const ShopCustomizationModal = ({ isOpen, onClose, onSave, initialData }) => {
  const [settings, setSettings] = useState({
    welcomeMessage: '',
    bannerUrl: '',
    logoUrl: '',
    primaryColor: '#10B981', // Default brand-green
    facebookLink: '',
    instagramLink: '',
    websiteLink: '',
    ...initialData
  });
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    data.append("cloud_name", process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);

    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        data
      );
      return res.data.secure_url;
    } catch (error) {
      console.error("Erreur upload Cloudinary", error);
      throw error;
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerFile(file);
    // Prévisualisation immédiate
    setSettings(prev => ({ ...prev, bannerUrl: URL.createObjectURL(file) }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    // Prévisualisation immédiate
    setSettings(prev => ({ ...prev, logoUrl: URL.createObjectURL(file) }));
  };

  const handleSaveInternal = async () => {
    setUploading(true);
    let currentSettings = { ...settings };

    try {
      // Upload des images seulement au moment de la sauvegarde
      if (logoFile) {
        const url = await uploadToCloudinary(logoFile);
        currentSettings.logoUrl = url;
      }
      if (bannerFile) {
        const url = await uploadToCloudinary(bannerFile);
        currentSettings.bannerUrl = url;
      }

      await onSave(currentSettings);
    } catch (error) {
      toast.error("Erreur lors de l'envoi des images.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (initialData) {
        setSettings(prev => ({ ...prev, ...initialData }));
        setLogoFile(null);
        setBannerFile(null);
    }
  }, [initialData]);

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
            <h3 className="text-xl font-bold text-gray-800">Personnaliser votre vitrine</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"><FiX size={24} /></button>
        </div>
        
        <div className="p-6 space-y-6 overflow-y-auto">
            {/* Logo Section */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Logo de la boutique</label>
                <div className="flex items-center gap-4">
                    {settings.logoUrl ? (
                        <div className="relative w-24 h-24 rounded-full overflow-hidden border border-gray-200 group shadow-sm">
                            <img src={settings.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button onClick={() => setSettings({ ...settings, logoUrl: '' })} className="bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"><FiTrash2 size={16} /></button>
                            </div>
                        </div>
                    ) : (
                        <label className="w-24 h-24 rounded-full border-2 border-gray-300 border-dashed flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                             <FiUploadCloud className="text-gray-400" />
                             <span className="text-[10px] text-gray-500 mt-1">Ajouter</span>
                             <input type="file" className="hidden" accept="image/*" onChange={handleLogoChange} disabled={uploading} />
                        </label>
                    )}
                    <div className="text-xs text-gray-500 flex-1">
                        Cette image apparaîtra comme photo de profil de votre boutique.
                    </div>
                </div>
            </div>

            {/* Banner Section */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Bannière d'accueil</label>
                {settings.bannerUrl ? (
                  <div className="relative w-full h-40 rounded-xl overflow-hidden border border-gray-200 group shadow-sm">
                    <img src={settings.bannerUrl} alt="Bannière" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button onClick={() => setSettings({ ...settings, bannerUrl: '' })} className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg"><FiTrash2 /></button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all group">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <>
                          <div className="p-3 bg-white rounded-full shadow-sm mb-3 group-hover:scale-110 transition-transform">
                              <FiUploadCloud className="w-6 h-6 text-brand-blue" />
                          </div>
                          <p className="text-sm font-medium text-gray-600">Cliquez pour ajouter une image</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG (Max 5Mo)</p>
                        </>
                    </div>
                    <input type="file" className="hidden" accept="image/*" onChange={handleBannerChange} disabled={uploading} />
                  </label>
                )}
            </div>

            {/* Welcome Message */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Message de bienvenue</label>
                <textarea 
                    name="welcomeMessage" 
                    rows="3" 
                    value={settings.welcomeMessage} 
                    onChange={handleChange} 
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all text-sm bg-gray-50 focus:bg-white" 
                    placeholder="Ex: Bienvenue chez nous ! Profitez de nos offres exceptionnelles..."
                ></textarea>
            </div>

            {/* Brand Color */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Couleur principale</label>
                <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl bg-gray-50">
                    <input type="color" name="primaryColor" value={settings.primaryColor} onChange={handleChange} className="h-10 w-10 rounded-lg cursor-pointer border-0 p-0 bg-transparent" />
                    <div className="flex-grow">
                        <p className="text-sm font-medium text-gray-900">Couleur de marque</p>
                        <p className="text-xs text-gray-500 font-mono uppercase">{settings.primaryColor}</p>
                    </div>
                </div>
            </div>

            {/* Social Links */}
            <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center"><FiGlobe className="mr-2"/> Réseaux Sociaux</h4>
                <div className="space-y-3">
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#1877F2] transition-colors">
                            <FiFacebook />
                        </div>
                        <input type="text" name="facebookLink" value={settings.facebookLink} onChange={handleChange} className="w-full pl-10 p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1877F2]/20 focus:border-[#1877F2] outline-none transition-all" placeholder="Lien Facebook" />
                    </div>
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#E4405F] transition-colors">
                            <FiInstagram />
                        </div>
                        <input type="text" name="instagramLink" value={settings.instagramLink} onChange={handleChange} className="w-full pl-10 p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#E4405F]/20 focus:border-[#E4405F] outline-none transition-all" placeholder="Lien Instagram" />
                    </div>
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-gray-800 transition-colors">
                            <FiGlobe />
                        </div>
                        <input type="text" name="websiteLink" value={settings.websiteLink} onChange={handleChange} className="w-full pl-10 p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-500/20 focus:border-gray-500 outline-none transition-all" placeholder="Site Web" />
                    </div>
                </div>
            </div>
        </div>
        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
            <button onClick={onClose} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded-xl transition-colors text-sm">Annuler</button>
            <button onClick={handleSaveInternal} disabled={uploading} className="px-5 py-2.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 text-sm shadow-lg shadow-gray-900/20 flex items-center">
                {uploading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>}
                Enregistrer
            </button>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { user, loading: authLoading, refreshUser, logout, openUpgradeModal } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    shopName: '',
    currency: 'CFA',
    phone: '',
    paymentPhone: '',
    address: '',
    oldPassword: '',
    newPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const isPremium = user?.plan === 'premium';
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentPlanToRenew, setPaymentPlanToRenew] = useState(null);
  const [paymentPriceToRenew, setPaymentPriceToRenew] = useState(0);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    const fetchFreshData = async () => {
      if (user) {
        try {
          // On récupère les données fraîches depuis le serveur pour s'assurer que le champ paymentPhone est bien là
          const { data } = await api.get('/profile'); 
          setProfileData(data);
          setFormData(prev => ({
            ...prev,
            shopName: data.shopName || user.shopName || '',
            currency: data.currency || user.currency || 'CFA',
            phone: data.phone || user.phone || '',
            paymentPhone: data.paymentPhone || user.paymentPhone || '',
            address: data.address || user.address || '',
          }));
        } catch (error) {
          // Si l'appel échoue, on utilise les données locales du contexte
          setFormData(prev => ({
            ...prev,
            shopName: user.shopName || '',
            currency: user.currency || 'CFA',
            phone: user.phone || '',
            paymentPhone: user.paymentPhone || '',
            address: user.address || '',
          }));
        }
        setLoading(false);
      }
    };
    fetchFreshData();
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const currentInfo = profileData || user || {};

    const payload = {
      shopName: formData.shopName,
      currency: formData.currency,
      phone: formData.phone,
      paymentPhone: formData.paymentPhone,
      address: formData.address,
      // Sécurité : Inclure les champs de personnalisation existants pour éviter qu'ils soient effacés
      // si le backend effectue un remplacement complet lors du PUT.
      welcomeMessage: currentInfo.welcomeMessage,
      bannerUrl: currentInfo.bannerUrl,
      logoUrl: currentInfo.logoUrl,
      primaryColor: currentInfo.primaryColor,
      facebookLink: currentInfo.facebookLink,
      instagramLink: currentInfo.instagramLink,
      websiteLink: currentInfo.websiteLink
    };
    if (formData.newPassword) {
      if (!formData.oldPassword) {
        toast.error("Veuillez entrer votre ancien mot de passe pour le changer.");
        return;
      }
      payload.oldPassword = formData.oldPassword;
      payload.newPassword = formData.newPassword;
    }

    try {
      await api.put('/profile', payload);
      toast.success("Profil mis à jour avec succès ! Les changements seront visibles après reconnexion.");
      // Si le nom de la boutique a changé, il faut rafraîchir le token pour mettre à jour la navbar
      // Cela suppose que le backend renvoie un nouveau token après la mise à jour du profil
      // ou que nous avons une fonction pour rafraîchir l'état de l'utilisateur.
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du profil.");
    }
  };

  const handleSaveCustomization = async (settings) => {
    try {
      // Construction d'un payload propre pour éviter d'envoyer des mots de passe vides
      const payload = {
        shopName: formData.shopName,
        currency: formData.currency,
        phone: formData.phone,
        paymentPhone: formData.paymentPhone,
        address: formData.address,
        welcomeMessage: settings.welcomeMessage,
        bannerUrl: settings.bannerUrl,
        logoUrl: settings.logoUrl,
        primaryColor: settings.primaryColor,
        facebookLink: settings.facebookLink,
        instagramLink: settings.instagramLink,
        websiteLink: settings.websiteLink
      };
      
      await api.put('/profile', payload);
      toast.success("Boutique personnalisée avec succès !");
      setIsCustomizationModalOpen(false);
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error("Erreur lors de la personnalisation.");
    }
  };

  const calculateRemainingDays = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Fonction robuste pour récupérer la date d'expiration peu importe la structure (aplati ou imbriqué)
  const getSubscriptionDate = () => {
    const sources = [profileData, user];
    for (const source of sources) {
      if (!source) continue;
      // Cas 1 : La date est directement sur l'objet (ex: objet Shop direct ou User aplati)
      if (source.subscription_ends_at) return source.subscription_ends_at;
      // Cas 2 : La date est dans un sous-objet 'shop' (ex: User populé)
      if (source.shop && typeof source.shop === 'object' && source.shop.subscription_ends_at) return source.shop.subscription_ends_at;
    }
    return null;
  };

  const subscriptionEndDate = getSubscriptionDate();
  const remainingDays = calculateRemainingDays(subscriptionEndDate);

  const handleRenewClick = () => {
    let price = 0;
    if (user?.plan === 'pro') price = 7500;
    if (user?.plan === 'premium') price = 25000;
    if (user?.billingCycle === 'yearly') {
        if (user?.plan === 'pro') price = 75000;
        if (user?.plan === 'premium') price = 250000;
    }
    setPaymentPlanToRenew(user?.plan);
    setPaymentPriceToRenew(price);
    setIsPaymentModalOpen(true);
  };

  const currentData = profileData || user;

  if (loading || authLoading) {
    return <Spinner message="Chargement du profil..." />;
  }

  const getPlanFeatures = (plan) => {
    switch(plan) {
        case 'premium': return ['Produits illimités', 'Boutique en ligne', 'Gestion d\'équipe', 'Rapports avancés', 'Support prioritaire'];
        case 'pro': return ['250 produits', 'Rappels SMS', 'Rapports de ventes', 'Gestion fournisseurs'];
        default: return ['25 produits', 'Suivi des dépenses', 'Carnet de clients', 'Mode Hors Ligne'];
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-12 px-4 sm:px-6">
      {/* Header Profile */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-0 mb-8 flex flex-col relative overflow-hidden group">
        {/* Background Cover */}
        <div className="h-32 w-full bg-gradient-to-r from-blue-600 to-brand-green relative">
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
        </div>
        
        <div className="px-8 pb-8 flex flex-col md:flex-row items-center md:items-end -mt-12 gap-6 relative z-10">
            {/* Avatar */}
            <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center text-5xl font-bold text-brand-blue border-4 border-white shadow-2xl overflow-hidden">
            {currentData?.logoUrl ? (
                <img src={currentData.logoUrl} alt="Logo" className="w-full h-full object-cover" />
            ) : (
                formData.shopName ? formData.shopName.charAt(0).toUpperCase() : <FiUser />
            )}
            </div>
            
            {/* Info */}
            <div className="flex-grow text-center md:text-left mb-2">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight font-poppins">{formData.shopName || 'Ma Boutique'}</h1>
                <div className="flex flex-col md:flex-row items-center gap-4 mt-2 text-gray-600 font-medium justify-center md:justify-start">
                    {user?.email && <span className="flex items-center"><FiMail className="mr-2 text-gray-400" /> {user.email}</span>}
                    {formData.phone && <span className="flex items-center"><FiPhone className="mr-2 text-gray-400" /> {formData.phone}</span>}
                </div>
            </div>

            {/* Badges */}
            <div className="flex flex-col items-center md:items-end gap-2 mb-2">
                 <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider shadow-sm ${user?.plan === 'premium' ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white' : user?.plan === 'pro' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    Plan {user?.plan || 'Essentiel'}
                </span>
                {user?.role === 'owner' && <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-100">Propriétaire</span>}
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Settings */}
        <div className="lg:col-span-2 space-y-8">
            <form onSubmit={handleSave}>
                {/* Shop Info Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8 hover:shadow-md transition-shadow duration-300">
                    <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center"><FiShoppingBag className="mr-2 text-brand-blue"/> Informations de la boutique</h3>
                    </div>
                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="shopName">Nom de la boutique</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiShoppingBag /></div>
                                <input id="shopName" type="text" value={formData.shopName} onChange={handleChange} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all font-medium text-gray-800" placeholder="Ex: Chez Moussa" />
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="phone">Téléphone (WhatsApp)</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiPhone /></div>
                                <input id="phone" type="text" value={formData.phone} onChange={handleChange} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all font-medium text-gray-800" placeholder="Ex: 77 000 00 00" />
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="paymentPhone">N° Paiement Wave/OM</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiCreditCard /></div>
                                <input id="paymentPhone" type="text" value={formData.paymentPhone} onChange={handleChange} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all font-medium text-gray-800" placeholder="Si différent du principal" />
                            </div>
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="address">Adresse</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiMapPin /></div>
                                <input id="address" type="text" value={formData.address} onChange={handleChange} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all font-medium text-gray-800" placeholder="Ex: Dakar, Médina" />
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="currency">Devise</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiDollarSign /></div>
                                <select id="currency" value={formData.currency} onChange={handleChange} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all appearance-none font-medium text-gray-800">
                                    <option value="CFA">CFA - Franc CFA</option>
                                    <option value="EUR">EUR - Euro</option>
                                    <option value="USD">USD - Dollar Américain</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Security Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8 hover:shadow-md transition-shadow duration-300">
                    <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center"><FiLock className="mr-2 text-brand-blue"/> Sécurité</h3>
                    </div>
                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="oldPassword">Ancien mot de passe</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiLock /></div>
                                <input id="oldPassword" type={showPassword ? "text" : "password"} value={formData.oldPassword} onChange={handleChange} className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all font-medium text-gray-800" placeholder="••••••••" />
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2" htmlFor="newPassword">Nouveau mot de passe</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors"><FiLock /></div>
                                <input id="newPassword" type={showPassword ? "text" : "password"} value={formData.newPassword} onChange={handleChange} className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all font-medium text-gray-800" placeholder="••••••••" />
                                <div className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-brand-blue transition-colors" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                    <button type="submit" className="bg-gray-900 hover:bg-black text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center text-lg">
                        <FiSave className="mr-2" /> Enregistrer
                    </button>
                </div>
            </form>
        </div>

        {/* Right Column: Subscription & Features */}
        <div className="lg:col-span-1 space-y-8">
            {isPremium && (
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-3xl shadow-2xl text-white relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-10 -mt-10 blur-2xl"></div>
                <h3 className="text-xl font-bold mb-4 flex items-center relative z-10">
                  <FiShoppingBag className="mr-2 text-brand-green"/> Boutique en Ligne
                </h3>
                <p className="text-gray-300 text-sm mb-6 relative z-10">
                  Votre vitrine est active. Partagez votre lien pour recevoir des commandes.
                </p>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/10 mb-6 relative z-10">
                  <a 
                    href={`/shop/${user?.username}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white font-mono text-sm break-all hover:text-brand-green transition-colors flex items-center"
                  >
                    <FiGlobe className="mr-2 flex-shrink-0" /> {`${window.location.host}/shop/${user?.username}`}
                  </a>
                </div>
                <button onClick={() => setIsCustomizationModalOpen(true)} className="w-full bg-brand-green text-white font-bold py-3 px-4 rounded-xl hover:bg-green-600 transition-colors flex items-center justify-center shadow-lg relative z-10">
                  <FiEdit3 className="mr-2" /> Personnaliser ma boutique
                </button>
              </div>
            )}

            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center"><FiCreditCard className="mr-2 text-brand-blue"/> Abonnement</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${user?.plan === 'premium' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{user?.plan}</span>
                </div>
                
                <div className="space-y-4">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Fonctionnalités incluses</p>
                    {getPlanFeatures(user?.plan).map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                            <div className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 flex-shrink-0"><FiCheck size={12} /></div>
                            {feature}
                        </div>
                    ))}
                </div>

                {user?.plan !== 'essentiel' && (
                    <div className={`mt-6 p-4 rounded-xl border ${remainingDays !== null && remainingDays <= 5 ? 'bg-red-50 border-red-100 text-red-800' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider flex items-center">
                                <FiClock className="mr-2"/> Validité
                            </span>
                            <span className="font-bold text-lg">{remainingDays !== null ? (remainingDays > 0 ? `${remainingDays} Jours` : 'Expiré') : 'Illimitée'}</span>
                        </div>
                        {remainingDays !== null && (
                            <div className="w-full bg-white/50 rounded-full h-2">
                                <div className={`h-2 rounded-full ${remainingDays <= 5 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, Math.max(0, (remainingDays / 30) * 100))}%` }}></div>
                            </div>
                        )}
                        <p className="text-xs mt-2 opacity-80">{subscriptionEndDate ? `Expire le ${new Date(subscriptionEndDate).toLocaleDateString('fr-FR')}` : "Aucune date d'expiration définie"}</p>
                    </div>
                )}

                <div className="mt-6 space-y-3">
                    {user?.plan !== 'essentiel' && remainingDays !== null && remainingDays <= 15 && (
                        <button onClick={handleRenewClick} className="w-full bg-green-100 text-green-700 font-bold py-3 px-4 rounded-xl hover:bg-green-200 transition-colors text-sm flex items-center justify-center border border-green-200">
                            <FiRefreshCw className="mr-2" /> Renouveler mon plan
                        </button>
                    )}
                    
                    {user?.plan !== 'premium' && (
                        <button onClick={() => openUpgradeModal()} className="w-full bg-gray-900 text-white font-bold py-3 px-4 rounded-xl hover:bg-gray-800 transition-colors text-sm">
                            {user?.plan === 'essentiel' ? 'Passer au plan supérieur' : 'Passer à Premium'}
                        </button>
                    )}
                </div>
            </div>
        </div>
      </div>

      <ShopCustomizationModal 
        isOpen={isCustomizationModalOpen} 
        onClose={() => setIsCustomizationModalOpen(false)} 
        onSave={handleSaveCustomization}
        initialData={{ 
            welcomeMessage: currentData?.welcomeMessage || '', 
            bannerUrl: currentData?.bannerUrl || '', 
            logoUrl: currentData?.logoUrl || '',
            primaryColor: currentData?.primaryColor || '#10B981',
            facebookLink: currentData?.facebookLink || '',
            instagramLink: currentData?.instagramLink || '',
            websiteLink: currentData?.websiteLink || ''
        }}
      />

      <PaymentModal 
        isOpen={isPaymentModalOpen} 
        onClose={() => setIsPaymentModalOpen(false)} 
        planName={paymentPlanToRenew} 
        price={paymentPriceToRenew} 
      />
    </div>
  );
};

export default Profile;